/**
 * Profile/Settings Page - Notion-Inspired Design
 * Manage business profile and account settings
 */

import React, { useEffect, useState } from 'react';
import { Card, Button, Input, Textarea, Badge, toast } from '../components/ui';
import { businessApi, Business } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

export const ProfilePage: React.FC = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Business | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editedProfile, setEditedProfile] = useState<Partial<Business>>({});

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    setIsLoading(true);
    try {
      const response = await businessApi.getProfile();
      setProfile(response.data.data);
      setEditedProfile({});
    } catch (error) {
      console.error('Failed to load profile:', error);
      toast.error('Failed to load profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!profile || Object.keys(editedProfile).length === 0) return;

    setIsSaving(true);
    try {
      const response = await businessApi.updateProfile(editedProfile);
      setProfile(response.data.data);
      setEditedProfile({});
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field: keyof Business, value: string) => {
    setEditedProfile((prev) => ({ ...prev, [field]: value }));
  };

  const getValue = (field: keyof Business) => {
    return (editedProfile[field] as string) ?? (profile?.[field] as string) ?? '';
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-8">
        <h1 className="text-h1 font-bold text-black">Profile & Settings</h1>
        <Card>
          <div className="h-96 bg-cream-100 rounded animate-pulse"></div>
        </Card>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex flex-col gap-8">
        <h1 className="text-h1 font-bold text-black">Profile & Settings</h1>
        <Card>
          <div className="text-center py-12 px-6">
            <p className="text-base text-black-lighter">Failed to load profile</p>
            <Button variant="primary" onClick={loadProfile} className="mt-4">
              Retry
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const hasChanges = Object.keys(editedProfile).length > 0;

  const handleChangePassword = () => {
    toast.info('Password change via email link coming soon. Please contact support@rewardables.app to reset your password.');
  };

  const handleEnable2FA = () => {
    toast.info('Two-factor authentication setup coming soon.');
  };

  const handleDeactivate = () => {
    if (window.confirm('Are you sure you want to deactivate your account? This will temporarily disable access for your customers.')) {
      toast.info('Account deactivation coming soon. Please contact support@rewardables.app for assistance.');
    }
  };

  const handleDeleteAccount = () => {
    if (window.confirm('Are you sure you want to permanently delete your account? This action CANNOT be undone and all data will be lost.')) {
      if (window.confirm('This is your last warning. All customer data, transactions, and settings will be permanently deleted. Continue?')) {
        toast.info('Account deletion coming soon. Please contact support@rewardables.app for assistance.');
      }
    }
  };

  return (
    <div className="flex flex-col gap-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <h1 className="text-h1 font-bold text-black">Profile & Settings</h1>
          <p className="text-base mt-2 text-black-lighter">
            Manage your business information and account settings
          </p>
        </div>
        {hasChanges && (
          <div className="flex gap-3 self-start sm:self-auto">
            <Button variant="secondary" onClick={() => setEditedProfile({})}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} isLoading={isSaving}>
              Save Changes
            </Button>
          </div>
        )}
      </div>

      {/* Account Info */}
      <Card>
        <div className="p-6 border-b border-border-light">
          <h3 className="text-h3 font-semibold text-black">Account Information</h3>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-base font-semibold text-black mb-1">Email Address</p>
            <p className="text-base text-black-lighter">{user?.email}</p>
          </div>
          <div>
            <p className="text-base font-semibold text-black mb-1">Account Status</p>
            <Badge
              variant={
                profile.status === 'ACTIVE'
                  ? 'success'
                  : profile.status === 'PENDING_SETUP'
                  ? 'warning'
                  : 'default'
              }
            >
              {profile.status.replace('_', ' ')}
            </Badge>
          </div>
          <div>
            <p className="text-base font-semibold text-black mb-1">Member Since</p>
            <p className="text-base text-black-lighter">
              {new Date(profile.createdAt).toLocaleDateString()}
            </p>
          </div>
        </div>
      </Card>

      {/* Business Information */}
      <Card>
        <div className="p-6 border-b border-border-light">
          <h3 className="text-h3 font-semibold text-black">Business Information</h3>
          <p className="text-base mt-1 text-black-lighter">
            Update your business details
          </p>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <Input
            label="Business Name"
            id="businessName"
            value={getValue('businessName')}
            onChange={(e) => handleChange('businessName', e.target.value)}
            placeholder="Enter business name"
          />
          <Input
            label="Category"
            id="category"
            value={getValue('category')}
            onChange={(e) => handleChange('category', e.target.value)}
            placeholder="e.g., Coffee Shop, Restaurant"
          />
          <Input
            label="Phone Number"
            id="phoneNumber"
            type="tel"
            value={getValue('phoneNumber')}
            onChange={(e) => handleChange('phoneNumber', e.target.value)}
            placeholder="(555) 123-4567"
          />
          <Input
            label="Website"
            id="website"
            type="url"
            value={getValue('website')}
            onChange={(e) => handleChange('website', e.target.value)}
            placeholder="https://yourbusiness.com"
          />
          <Input
            label="Online Ordering Link"
            id="orderLink"
            type="url"
            value={getValue('orderLink')}
            onChange={(e) => handleChange('orderLink', e.target.value)}
            placeholder="https://order.yourbusiness.com"
            helperText="Link customers can use to order online (Square, UberEats, DoorDash, etc.)"
          />
          <div className="md:col-span-2">
            <Input
              label="Address"
              id="address"
              value={getValue('address')}
              onChange={(e) => handleChange('address', e.target.value)}
              placeholder="123 Main St, City, State 12345"
            />
          </div>
          <div className="md:col-span-2">
            <Textarea
              label="Description"
              id="description"
              value={getValue('description')}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Tell customers about your business..."
              rows={4}
            />
          </div>
        </div>
      </Card>

      {/* Security */}
      <Card>
        <div className="p-6 border-b border-border-light">
          <h3 className="text-h3 font-semibold text-black">Security</h3>
          <p className="text-base mt-1 text-black-lighter">
            Manage your account security
          </p>
        </div>
        <div className="p-6 flex flex-col gap-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-base font-semibold text-black">Password</p>
              <p className="text-base text-black-lighter">••••••••••••</p>
            </div>
            <Button variant="secondary" size="sm" onClick={handleChangePassword}>
              Change Password
            </Button>
          </div>
          <div className="border-t border-border-light"></div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-base font-semibold text-black">Two-Factor Authentication</p>
              <p className="text-base text-black-lighter">Add an extra layer of security</p>
            </div>
            <Button variant="secondary" size="sm" onClick={handleEnable2FA}>
              Enable 2FA
            </Button>
          </div>
        </div>
      </Card>

      {/* Danger Zone */}
      <Card>
        <div className="p-6 border-b border-border-light">
          <h3 className="text-h3 font-semibold text-black">Danger Zone</h3>
          <p className="text-base mt-1 text-black-lighter">
            Irreversible actions
          </p>
        </div>
        <div className="p-6 flex flex-col gap-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-base font-semibold text-error-600">Deactivate Account</p>
              <p className="text-base text-black-lighter">
                Temporarily disable your account
              </p>
            </div>
            <Button variant="tertiary" size="sm" className="text-error-600 hover:bg-error-50" onClick={handleDeactivate}>
              Deactivate
            </Button>
          </div>
          <div className="border-t border-border-light"></div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-base font-semibold text-error-600">Delete Account</p>
              <p className="text-base text-black-lighter">
                Permanently delete your account and all data
              </p>
            </div>
            <Button variant="tertiary" size="sm" className="text-error-600 hover:bg-error-50" onClick={handleDeleteAccount}>
              Delete Account
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
